import AdyenCheckout from '@adyen/adyen-web'
import Core from '@adyen/adyen-web/dist/types/core'
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types'
import { useContext, useEffect, useState } from 'react'

import bugsnagClient from '@/lib/bugsnag'
import logger from '@/lib/logger'
import errorMessageHandler from '@/utils/errorMessageHandler'
import useAdyenCardStyles from '@/utils/hooks/useAdyenCardStyles'
import useAdyenPaymentMethods from '@/utils/hooks/useAdyenPaymentMethods'
import useSdkEvents from '@/utils/hooks/useSdkEvents'
import { isInIframe, isInSdkIframe } from '@/utils/isInIframe'
import isNetworkError from '@/utils/isNetworkError'
import StoresContext from '@/utils/StoresContext'

export interface AdyenCheckoutProps {
  amount: number
  onSubmit: NonNullable<CoreOptions['onSubmit']>
}

export default function useAdyenCheckout({ amount, onSubmit }: AdyenCheckoutProps) {
  const { appStore } = useContext(StoresContext)
  const { sendRedirect } = useSdkEvents()
  const [checkout, setCheckout] = useState<Core | null>(null)

  const cardStyles = useAdyenCardStyles()
  const paymentMethods = useAdyenPaymentMethods(amount)

  useEffect(() => {
    if (!paymentMethods) {
      return
    }

    const initialize = async () => {
      const configuration: CoreOptions = {
        amount: {
          value: amount,
          currency: appStore.state.currency
        },
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: false,
            holderNameRequired: false,
            enableStoreDetails: false, // recurring payments
            hideCVC: false,
            styles: cardStyles
          },
          storedCard: {
            styles: cardStyles
          },
          paywithgoogle: {
            emailRequired: false,
            buttonType: 'pay'
          }
        },
        paymentMethodsResponse: paymentMethods,
        showPayButton: true,
        locale: appStore.state.language,
        environment: appStore.state.adyenEnv,
        clientKey: appStore.state.adyenPublicKey,
        beforeRedirect: async (resolve, reject, data) => {
          if (isInSdkIframe(appStore)) {
            try {
              /*
               * For GET method (at least iDeal), redirect in iFrame is not working for security reasons :
               * - DOMException: Failed to read a named property 'href' from 'Location': Blocked a frame with origin "an_url" from accessing a cross-origin frame.
               * - Refused to frame 'https://ideal.ing.nl/' because an ancestor violates the following Content Security Policy directive: "frame-ancestors 'self' api.ideal.ing.nl"
               *
               * upd. After upgrading adyen-web library from v5.52.0 to v5.71.2 POST method redirection no longer works fine as well
               *
               * Solution is to redirect on the widget SDK
               */
              sendRedirect(data.method, data.url, data.data)
            } catch (err) {
              logger.error('failed to post message to widget sdk', err)
            }
            // This reject prevents adyen redirection handling
            reject()
          }

          // If not in iframe continue default redirection
          resolve()
        },
        onAdditionalDetails: (_state, _dropin) => {
          logger.info("onAdditionalDetails that is required so we can't remove it")
          // send a bugsnag to make sure later that it is really not used at all
        },
        onSubmit: async (state, dropin) => {
          if (!state.isValid) {
            return
          }

          try {
            appStore.state.error = null

            await onSubmit(state, dropin)
          } catch (error) {
            dropin.setStatus('ready')
            appStore.state.error = errorMessageHandler(error)
            if (!isNetworkError(error)) {
              logger.error(error)
              bugsnagClient.notify(error)
            }
          }
        }
      }

      try {
        setCheckout(await AdyenCheckout(configuration))
      } catch (error) {
        if (!isNetworkError(error)) {
          logger.error(error)
          bugsnagClient.notify(error)
        }
      }
    }

    if (isInIframe() && !isInSdkIframe(appStore)) {
      bugsnagClient.notify(
        new Error(
          'This booking widget is in an iframe that is not the sdk iframe, this may prevent some payment methods from working properly'
        ),
        (event) => {
          event.severity = 'warning'
          event.addMetadata('widget integration', {
            windowSelf: window?.self?.location?.href,
            windowTop: window?.top?.location?.href,
            appStoreStateSdk: appStore?.state?.sdk,
            appStoreStateIsFullscreen: appStore?.state?.isFullscreen
          })
        }
      )
    }

    initialize()
  }, [
    amount,
    cardStyles,
    paymentMethods,
    onSubmit,
    sendRedirect,
    appStore,
    appStore.state.currency,
    appStore.state.language,
    appStore.state.query,
    appStore.state.adyenEnv,
    appStore.state.adyenPublicKey
  ])

  return checkout
}
